import { CategoryType, getCtaxTypeName } from "enums";
import {
  ApplyItemDetailModel,
  CategoryModel,
  HideListItemModel,
  LimitedListItemModel,
} from "models";
import React from "react";
import { Utils } from "utils";
import Chip from "./common/Chip";
import Tag from "./common/chip/Tag";
import Image from "./common/images/Image";
import { CommonFileModel } from "models/CommonFileModel";

type PROPS = {
  category: CategoryModel[];
  limitedLists: LimitedListItemModel[];
  hideLists: HideListItemModel[];
  data: ApplyItemDetailModel;
  deepFileChanged: {
    item_main_image: CommonFileModel,
    item_sub_image:  CommonFileModel[],
    item_append_pdf:  CommonFileModel
  } | null
};

const ExhibitFormsConfirm: React.FC<PROPS> = (props) => {
  const { deepFileChanged, data } = props;

  const getScopeText = () => {
    if (data.list_type === 1) {
      const target = props.limitedLists.find(
        (v) => v.limited_list_id === data.list_id
      );
      if (target) {
        return target.limited_list_name + "(限定公開)";
      }
    } else if (data.list_type === 2) {
      const target = props.hideLists.find(
        (v) => v.hide_list_id === data.list_id
      );
      if (target) {
        return target.hide_list_name + "(非公開)";
      }
    }
    return "すべての範囲に公開";
  };
  const showImageNew = deepFileChanged?.item_main_image && data.apply_item_main_image?.is_new;
  const showImageSubNew = deepFileChanged?.item_sub_image && deepFileChanged?.item_sub_image && data.apply_item_sub_image.some((i: any) => !!i?.is_new);
  const showImageFileNew =  deepFileChanged?.item_append_pdf && data.apply_item_append_pdf?.is_new;

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="w-3/12">商品名</div>
        <div className="w-9/12">{data.apply_item_name}</div>
      </div>
      {showImageNew && (
        <div className="flex justify-between items-start">
          <div className="w-3/12">商品メイン画像(変更前)</div>
          <div className="w-9/12 flex justify-start">
            <div className="w-1/5">
              {Utils.hasFile(deepFileChanged.item_main_image) && (
                <Image data={deepFileChanged.item_main_image} show_file_name />
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between items-start">
        <div className="w-3/12">商品メイン画像{showImageNew ? '(変更後)' : ''}</div>
        <div className="w-9/12 flex justify-start">
          <div className="w-1/5">
            {Utils.hasFile(data.apply_item_main_image) && (
              <Image data={data.apply_item_main_image} show_file_name />
            )}
          </div>
        </div>
      </div>
      {showImageSubNew && (
        <div className="flex justify-between items-start">
        <div className="w-3/12">商品サブ画像(変更前)</div>
        <div className="w-9/12 grid grid-cols-5 gap-2 justify-start items-end">
          {deepFileChanged.item_sub_image.map((v, i) => {
            return <Image key={i} data={v} show_file_name />;
          })}
        </div>
      </div>
      )}
      <div className="flex justify-between items-start">
        <div className="w-3/12">商品サブ画像{showImageSubNew ? '(変更後)' : ''}</div>
        <div className="w-9/12 grid grid-cols-5 gap-2 justify-start items-end">
          {data.apply_item_sub_image.map((v, i) => {
            return <Image key={i} data={v} show_file_name />;
          })}
        </div>
      </div>
      <div className="flex justify-between items-center">
        <div className="w-3/12">JANコード</div>
        <div className="w-9/12">{data.apply_jan_code}</div>
      </div>
      <div className="flex justify-between items-start">
        <div className="w-3/12">商品カテゴリ</div>
        <div className="w-9/12">
          {data.apply_item_category.map((v, i) => {
             const main = Utils.Category.pickCategory(
               v.apply_item_large_category_id,
               props.category,
               CategoryType.LARGE
             );
             const medium = Utils.Category.pickCategory(
               v.apply_item_medium_category_id ?? "",
               props.category,
               CategoryType.MIDDLE
             );
             const small = Utils.Category.pickCategory(
               v.apply_item_small_category_id ?? "",
               props.category,
               CategoryType.SMALL
             );
            return (
              <div key={i} className="mb-2">
                <div>
                  大分類:&nbsp;
                  {main?.name ?? "-"}
                </div>
                <div>{!!medium && <div>中分類: {medium?.name}</div>}</div>
                <div>{!!small && <div>小分類: {small?.name}</div>}</div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex justify-between items-start">
        <div className="w-3/12">商品詳細文</div>
        <div className="w-9/12 whitespace-pre-line break-words">
          {data.apply_item_description}
        </div>
      </div>
      {showImageFileNew && (
        <div className="flex justify-between items-center">
          <div className="w-3/12">商品詳細添付(PDF)(変更前)</div>
          <div className="w-9/12">{deepFileChanged.item_append_pdf?.file_name}</div>
        </div>
      )}
      <div className="flex justify-between items-center">
        <div className="w-3/12">商品詳細添付(PDF){showImageFileNew ? '(変更後)' : ''}</div>
        <div className={`w-9/12 ${showImageFileNew ? 'text-red-500' : ''}`}>{data.apply_item_append_pdf?.file_name}</div>
      </div>
      <div className="flex justify-between items-center">
        <div className="w-3/12">公開範囲設定</div>
        <div className="w-9/12">{getScopeText()}</div>
      </div>
      <div className="flex justify-between items-center">
        <div className="w-3/12">個別見積</div>
        <div className="w-9/12">
          {data.apply_is_discretionary_price ? "あり" : "なし"}
        </div>
      </div>
      <div className="flex justify-between items-start">
        <div className="w-3/12">価格(税抜)</div>
        <div className="w-9/12">
          {data.apply_unit_price === "" ? (
            <>-</>
          ) : (
            <>{Utils.addComma(data.apply_unit_price)}円</>
          )}
        </div>
      </div>
      <div className="flex justify-between items-start">
        <div className="w-3/12">課税区分</div>
        <div className="w-9/12">{getCtaxTypeName(data.apply_ctax_type)}</div>
      </div>
      <div className="flex items-start">
        <div className="w-3/12 pt-2">関連商品</div>
        <div className="flex-1 flex flex-wrap gap-2">
          {data.apply_recommend_item.map((item, index) => {
            return <Tag key={index} text={item.apply_recommend_item_name} />;
          })}
        </div>
      </div>
      <div className="flex items-start">
        <div className="w-3/12 pt-2">タグ</div>
        <div className="flex-1 flex flex-wrap gap-2">
          {data.apply_tag.map((v, i) => {
            return (
              <Chip
                key={i}
                text={v.apply_item_tag_name}
                color="#d1d5db"
                textColor="#000000"
              />
            );
          })}
        </div>
      </div>
      <div className="flex justify-between items-start">
        <div className="w-3/12">備考</div>
        <div className="w-9/12 whitespace-pre-line break-words break-all">{data.apply_item_note}</div>
      </div>
    </div>
  );
};

export default ExhibitFormsConfirm;
